import dynamic from 'next/dynamic';

import { DynamicIcon } from '@natu/ui/DynamicIcon';
import { cn } from '@natu/utils/cn';

import {
  getAssetFromStoryblok,
  getDescriptiveText,
  getLinkPropsFromStoryblok,
  resolveStoryblokStyles,
  sbEditable,
  type SBProps,
  type Spacing,
  type StoryblokAsset,
  type StoryblokLink,
} from '../../../../utils';
import { LinkWithConditionalEvent } from './LinkWithConditionalEvent';

const ResponsiveImage = dynamic(() =>
  import('@natu/ui/ResponsiveImage').then(mod => mod.ResponsiveImage),
);

interface SBCaseStudyCardProps {
  stretch?: boolean;
  evenStretch?: boolean;
  companyName?: string;
  projectCategory?: string;
  title?: string;
  description?: string;
  ratingValue?: string;
  ratingLabel?: string;
  firstStatisticFirstValue?: string;
  firstStatisticSecondValue?: string;
  secondStatisticFirstValue?: string;
  secondStatisticSecondValue?: string;
  caseStudyUrl?: StoryblokLink;
  image?: StoryblokAsset;
  mbMobile?: Spacing;
  mbTablet?: Spacing;
  mbDesktop?: Spacing;
  mtMobile?: Spacing;
  mtTablet?: Spacing;
  mtDesktop?: Spacing;
}

export const SBCaseStudyCard = ({ blok }: SBProps<SBCaseStudyCardProps>) => {
  const {
    caseStudyUrl,
    companyName,
    description,
    evenStretch,
    firstStatisticFirstValue,
    firstStatisticSecondValue,
    image,
    mbDesktop,
    mbMobile,
    mbTablet,
    mtDesktop,
    mtMobile,
    mtTablet,
    projectCategory,
    ratingLabel,
    ratingValue,
    secondStatisticFirstValue,
    secondStatisticSecondValue,
    stretch,
    title,
  } = blok;

  const className = resolveStoryblokStyles({
    mb: mbMobile,
    mbTablet,
    mbDesktop,
    mt: mtMobile,
    mtTablet,
    mtDesktop,
  });

  const hideGrid =
    !firstStatisticFirstValue &&
    !firstStatisticSecondValue &&
    !ratingLabel &&
    !ratingValue &&
    !secondStatisticFirstValue &&
    !secondStatisticSecondValue;

  const resolvedImage = getAssetFromStoryblok(image);
  const resolvedCaseStudyUrl = getLinkPropsFromStoryblok(caseStudyUrl);
  const buttonLabel = 'Learn more';

  const { descriptiveText } = getDescriptiveText({
    title: buttonLabel,
    link: resolvedCaseStudyUrl,
  });

  const styles = {
    wrapper: cn(
      className,
      'border-naturaily-azureGray h-fit rounded-2xl border',
      stretch && 'lg:grid lg:grid-cols-[520px_auto]',
      stretch && evenStretch && 'lg:grid lg:grid-cols-2',
    ),
    card: cn(
      'bg-naturaily-azureGray rounded-t-2xl p-6 lg:p-8',
      stretch && 'lg:rounded-bl-2xl lg:rounded-tl-2xl lg:rounded-tr-none lg:py-10 lg:pl-8 lg:pr-20',
      stretch && evenStretch && 'p-6 lg:p-8',
    ),
    tags: cn(
      'mb-4 flex flex-col gap-y-1 lg:flex-row lg:flex-wrap lg:gap-x-3',
      stretch && !evenStretch ? 'lg:mb-14' : 'lg:mb-2',
    ),
    company: 'text-primary-700 text-sm font-semibold -tracking-[0.4%]',
    category: 'text-sm font-semibold -tracking-[0.4%] text-gray-400',
    grid: 'mb-8 grid grid-cols-3 gap-x-6',
    title: cn(
      'hover:text-primary=600 text-xl font-semibold -tracking-[0.2px] text-gray-900 transition-colors',
      stretch ? 'xl:text-display-md-34 mb-6' : 'mb-2 xl:text-2xl',
    ),
    description: 'mb-6 text-sm text-gray-500 lg:mb-8 xl:text-base',
    image: cn(
      'pointer-events-none h-full rounded-b-2xl object-cover',
      !stretch && 'aspect-video',
      stretch && 'max-h-none lg:rounded-b-none lg:rounded-br-2xl lg:rounded-tr-2xl',
    ),
    button: 'flex items-center gap-x-2 uppercase',
  };

  return (
    <div className={styles.wrapper} {...sbEditable(blok)}>
      <div className={styles.card}>
        {(projectCategory || companyName) && (
          <div className={styles.tags}>
            <p className={styles.company}>{companyName}</p>
            <p className={styles.category}>{projectCategory}</p>
          </div>
        )}
        {title && (
          <LinkWithConditionalEvent className={styles.title} link={resolvedCaseStudyUrl}>
            {title}
          </LinkWithConditionalEvent>
        )}
        {description && <p className={styles.description}>{description}</p>}
        {!hideGrid && (
          <div className={styles.grid}>
            {ratingValue && (
              <div className="flex gap-x-2">
                <DynamicIcon className="shrink-0" icon="star" />
                <GridItem value={ratingValue} label={ratingLabel} />
              </div>
            )}
            <GridItem value={firstStatisticFirstValue} label={firstStatisticSecondValue} />
            <GridItem value={secondStatisticFirstValue} label={secondStatisticSecondValue} />
          </div>
        )}
        <LinkWithConditionalEvent
          className={styles.button}
          link={resolvedCaseStudyUrl}
          descriptiveText={descriptiveText}
          isButton
        >
          {buttonLabel}
          <DynamicIcon icon="arrowRight" />
        </LinkWithConditionalEvent>
      </div>
      {resolvedImage.src && (
        <LinkWithConditionalEvent link={resolvedCaseStudyUrl}>
          <ResponsiveImage {...resolvedImage} className={styles.image} />
        </LinkWithConditionalEvent>
      )}
    </div>
  );
};

const GridItem = ({ value, label }: { value?: string; label?: string }) => {
  if (!value && !label) return null;

  return (
    <div className="flex flex-col gap-y-0.5">
      {value && (
        <p className="text-base font-bold -tracking-[0.2px] text-gray-900 xl:text-lg">{value}</p>
      )}
      {label && <p className="text-xs -tracking-[0.4%] text-gray-400 xl:text-sm">{label}</p>}
    </div>
  );
};
